import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@interlayer/js/components/common/Button';

import { useGameCodeLaunch } from '@interlayer/js/app/components/main/Games/hooks/useGameLaunch';
import { CTA_ACTIONS } from '@interlayer/js/app/components/main/Marquee/constants';
import { useParseHub2Components } from '@interlayer/js/app/hooks/useParseHub2Components';
import useSegmentation from '@interlayer/js/app/hooks/useSegmentation';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { MarqueeCta, MarqueeItem, TMarqueeProps } from './types';

import style from './Marquee.scss';

const MarqueeButton: React.FC<MarqueeCta> = (props) => {
    const gameLaunch = useGameCodeLaunch(props.gameCode || '');

    return (
        <Button
            view={props.type}
            size="small"
            {
                ...props.action === CTA_ACTIONS.linkToPage
                    ? { href: props.url }
                    : { onClick: gameLaunch }
            }
        >
            {props.label}
        </Button>
    );
};

export const Marquee: React.FC<TMarqueeProps> = ({ config: { MarqueeItems = { items: [] }, color }, setShouldRenderPortlet }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const blockRef = useRef<HTMLDivElement>(null);
    const [counts, setCounts] = useState(1);
    const segmentation = useSegmentation();
    const parseHub2Components = useParseHub2Components();

    const items = useMemo(() => {
        const filtered = MarqueeItems.items.filter(item => {
            return item.isEnabled && segmentation(item.segmentation);
        });

        if (!filtered.length) {
            if (setShouldRenderPortlet) {
                setShouldRenderPortlet(false);
            }

            return [];
        }

        return Array.from({ length: counts * filtered.length })
            .map((_, i) => filtered[i % filtered.length])
            .map((marquee: MarqueeItem, index) => (
                <span
                    data-testid="Marquee_block"
                    key={index}
                    className={style.blockText}
                >
                    {parseHub2Components(marquee.text)}

                    {marquee.isCtaEnabled && marquee.ctas &&
                        marquee.ctas.map((cta, i) => (
                            <MarqueeButton key={i} {...cta} />
                        ))
                    }
                </span>
            ));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [MarqueeItems.items, counts, parseHub2Components, segmentation]);

    useEffect(() => {
        if (blockRef.current && containerRef.current && blockRef.current?.offsetWidth * 1.5 < containerRef.current?.offsetWidth) {
            setCounts(Math.round(containerRef.current.offsetWidth * 1.5 / blockRef.current.offsetWidth));
        }
    }, []);

    if (!items.length) {
        return null;
    }

    return (
        <div
            data-testid="Marquee_container"
            className={clsx(style.container, style[color])}
        >
            <div
                ref={containerRef}
                className={style.scrollable}
                style={
                    containerRef.current?.offsetWidth
                        ? { animationDuration: `${containerRef.current.offsetWidth / 100}s` }
                        : {}
                }
            >
                <div className={style.blockCopy}>
                    <div ref={blockRef} className={style.block}>
                        {items}
                    </div>
                </div>

                <div className={style.blockCopy}>
                    <div className={style.block}>
                        {items}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Marquee;
